import { FC, useContext } from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  makeStyles,
} from "@material-ui/core";

import { OrderBookPagesHeader } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { BaseContainer } from "containers";
import { AppContext } from "contexts";
import * as routes from "routes";
import { useAppConfigs } from "services/appConfigs";
import { OPEN_AUCTION_STATES } from "services/auctions/constants";
import { useInventoryListingsPaginated } from "services/inventory";
import { LISTING_DEAL_STATES } from "services/listings/constants";
import theme from "theme";

import ListingSection from "./components/ListingSection";

const useStyles = makeStyles(() =>
  createStyles({
    sectionsContainer: {
      minHeight: "80vh",
      paddingTop: theme.spacing(5),
      position: "relative",
    },
  }),
);

const Overview: FC = () => {
  const classes = useStyles();

  const appContext = useContext(AppContext);
  const appConfigsHeaderStats = useAppConfigs(["COMPLETED_TRANSACTIONS_COUNT"]);
  const userId = appContext.user?.userId;

  const listingsQueryOptions = {
    featuredListings: {
      filter: {
        "state.in": [
          "accepting_final_offers",
          "open",
          ,
          ...OPEN_AUCTION_STATES,
        ],
      },
      sort: [
        "accepting_final_offers_start_at",
        "-is_open_auction",
        "-close_date",
        "featured_position",
      ],
      page: "1",
      pageSize: "9",
    },
    myListings: {
      filter: {
        seller_id: [userId],
        "state.in": ["open", ...LISTING_DEAL_STATES],
      },
      sort: ["featured_position", "-published_date"],
      page: "1",
      pageSize: "3",
    },
    closedListings: {
      filter: {
        "state.in": LISTING_DEAL_STATES,
      },
      sort: ["-deal_date"],
      page: "1",
      pageSize: "3",
    },
  };

  const featuredListingQueries = useInventoryListingsPaginated(
    listingsQueryOptions.featuredListings,
  );

  const myListingQueries = useInventoryListingsPaginated(
    userId ? listingsQueryOptions.myListings : null,
  );

  const closedListingQueries = useInventoryListingsPaginated(
    listingsQueryOptions.closedListings,
  );

  const isListingsLoading =
    featuredListingQueries.isLoading ||
    myListingQueries.isLoading ||
    closedListingQueries.isLoading;

  const isListingsFetched =
    featuredListingQueries.isFetched && userId
      ? myListingQueries.isFetched
      : myListingQueries.isIdle && closedListingQueries.isFetched;

  const featuredListings = featuredListingQueries.data?.pages?.[0].results?.filter(
    listing => {
      if (
        listing.kind === "auction" ||
        listing.is_featured ||
        listing.state === "accepting_final_offers"
      ) {
        return listing;
      }
    },
    [],
  );

  const myListings = myListingQueries.data?.pages?.[0].results;
  const closedListings = closedListingQueries.data?.pages?.[0].results;

  const sections = [
    {
      title: "Featured Listings",
      listings: featuredListings,
      button: {
        label: "View All Open Listings",
        path: routes.orderBookOpenListings(userId),
      },
    },
    {
      title: "My Listings",
      listings: myListings,
      button: {
        label: "View All My Listings",
        path: routes.orderBookMyListings(userId),
      },
    },
    {
      title: "Explore some of our past deals",
      listings: closedListings,
      button: {
        label: "View Past Deals",
        path: routes.orderBookPastDeals(userId),
      },
      isSecondarySection: true,
    },
  ];

  return (
    <BaseContainer
      renderFullWidthContent={() => (
        <OrderBookPagesHeader
          h1Content={{ default: "Royalty Offerings" }}
          subtitle1Content={`Access to uncorrelated, royalty-generating intellectual property. More than ${
            appConfigsHeaderStats.data?.COMPLETED_TRANSACTIONS_COUNT ||
            EMPTY_VALUE_PLACEHOLDER
          } deals completed on Royalty Exchange and new assets added each week.`}
          headerButtonContent={{
            label: "View All Open Listings",
            path: routes.orderBookOpenListings(userId),
          }}
        />
      )}
    >
      <Box className={classes.sectionsContainer}>
        {isListingsLoading && (
          <CircularProgress
            style={{
              position: "absolute",
              left: "50%",
              top: "100px",
              marginLeft: "-20px",
              marginTop: "-20px",
            }}
          />
        )}
        {sections.map(section => {
          if (isListingsFetched && !!section.listings?.length) {
            return (
              <ListingSection
                title={section.title}
                listings={section.listings}
                button={section.button}
                key={section.title}
                isSecondarySection={section.isSecondarySection}
              />
            );
          }
        })}
      </Box>
    </BaseContainer>
  );
};

export default Overview;
